<template>
    <kiosk-org-unit-details :teamId="props.teamId" :orgUnitId="props.orgUnitId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-sm) justify-center">
            <page-callout section="kiosk-org-unit" class="mb-4" />

            <page-header>Details</page-header>

            <vx-expandable-card class="mb-6">
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col gap-1">
                            <strong>Name:</strong>
                            <div class="text-sm text-slate-500 flex items-center gap-1">
                                <vx-badge size="xs" color="slate">{{ orgUnit.id }}</vx-badge> {{ orgUnit.name }}
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Team:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="teamLoading"></vx-icon-loading>
                                <div v-else-if="team" class="flex items-center gap-2">
                                    <vx-badge size="xs" color="slate">{{ team.id }}</vx-badge>
                                    <span>{{ team.name }}</span>
                                    <vx-button
                                        class="ml-1"
                                        size="xs"
                                        color="muted"
                                        data-test="go-to-team"
                                        :href="`/kiosk/teams/${team.id}`"
                                    >
                                        Go to Team
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Team Owner:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="teamOwnerLoading"></vx-icon-loading>
                                <div v-else-if="teamOwner" class="flex items-center gap-2">
                                    <div>{{ teamOwner.name }}</div>
                                    <vx-button
                                        color="muted"
                                        size="xs"
                                        data-test="go-to-owner"
                                        :href="`/kiosk/users/${teamOwner.id}`"
                                    >
                                        Go to Owner
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>OU Owner:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading
                                    spin
                                    v-if="orgUnitOwnerLoading"
                                    data-test="org-unit-owner-loading"
                                ></vx-icon-loading>
                                <div v-else-if="orgUnitOwner" class="flex items-center gap-2">
                                    <div>{{ orgUnitOwner.name }}</div>
                                    <vx-button
                                        color="muted"
                                        size="xs"
                                        data-test="go-to-org-unit-owner"
                                        :href="`/kiosk/users/${orgUnitOwner.id}`"
                                    >
                                        Go to Owner
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Users:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="usersCountLoading"></vx-icon-loading>
                                <div v-else-if="usersCount != undefined" class="flex items-center gap-2">
                                    {{ usersCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Pending Invites:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="invitationsCountLoading"></vx-icon-loading>
                                <div v-else-if="invitationsCount != undefined" class="flex items-center gap-2">
                                    {{ invitationsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Groups:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="groupsCountLoading"></vx-icon-loading>
                                <div v-else-if="groupsCount != undefined" class="flex items-center gap-2">
                                    {{ groupsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
        </div>
    </kiosk-org-unit-details>
</template>

<script setup>
import KioskOrgUnitDetails from '~/components/kiosk/kiosk-org-units/details/KioskOrgUnitDetails.vue';
import { VxBadge, VxButton, VxExpandableCard, VxIconLoading } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { useOrgUnitDetails } from '~/components/kiosk/kiosk-org-units/composables/useOrgUnitDetails';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';

const {
    orgUnit,
    onMountedOrgUnit,

    team,
    teamLoading,

    teamOwner,
    teamOwnerLoading,

    orgUnitOwner,
    orgUnitOwnerLoading,
    getOrgUnitOwner,

    usersCount,
    usersCountLoading,
    getUsersCount,

    invitationsCount,
    invitationsCountLoading,
    getInvitationsCount,

    groupsCount,
    groupsCountLoading,
    getGroupsCount,
} = useOrgUnitDetails();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
});

onMountedOrgUnit(() => {
    getOrgUnitOwner();
    getUsersCount();
    getInvitationsCount();
    getGroupsCount();
});
</script>
